/**
 * AddAccommodation Component
 * 
 * A modal component for adding or editing accommodation details in the trip planner.
 * Supports multiple accommodation types with dynamic form generation and validation.
 * 
 * Features:
 * - Dynamic accommodation type selection
 * - Type-specific form rendering
 * - Data validation and error handling
 * - Edit mode support with pre-filled data
 * - Modular form structure for different accommodation types
 * 
 * Props:
 * @prop {boolean} isOpen - Controls modal visibility
 * @prop {Function} onClose - Callback to handle modal closure
 * @prop {Function} onSave - Callback to save accommodation data
 * @prop {Object} [initialAccommodation=null] - Pre-existing accommodation data for edit mode
 * @prop {React.ReactNode} [warningBanner=null] - Optional warning banner to display at top of modal
 * 
 * State Management:
 * - Handles form data persistence
 * - Manages type selection
 * - Tracks validation errors
 * - Maintains modal state
 */

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import AddHotelForm from './AddHotelForm';
import { accommodationService } from '../../services/accommodationService';
import './AddAccommodation.css';

const AddAccommodation = ({ isOpen, onClose, onSave, initialAccommodation = null, warningBanner = null }) => {
  // Component State
  const [selectedType, setSelectedType] = useState(null);       // Currently selected accommodation type
  const [formData, setFormData] = useState({ details: {} });   // Form data with nested details object
  const [error, setError] = useState('');                      // Validation/error messages
  const [accommodationTypes, setAccommodationTypes] = useState({}); // Available accommodation types

  /**
     * Dynamic Accommodation Types Loading
     * 
     * Loads accommodation type configurations when modal opens.
     * Uses dynamic import to reduce initial bundle size.
     * 
     * @effect
     * @dependency {boolean} isOpen - Modal visibility state
     */
  useEffect(() => {
    if (isOpen) {
      import('../../constants/accommodationTypes.ts').then(module => {
        setAccommodationTypes(module.ACCOMMODATION_TYPES);
      });
    }
  }, [isOpen]);

  /**
     * Initial Data Handler
     * 
     * Manages form state initialization for both new and edit modes.
     * Resets form when modal opens/closes.
     * 
     * @effect
     * @dependencies {boolean} isOpen - Modal state
     * @dependencies {Object} initialAccommodation - Pre-existing data
     */
  useEffect(() => {
    if (isOpen && initialAccommodation) {
      setSelectedType(initialAccommodation.type);
      setFormData(initialAccommodation);
    } else if (isOpen) {
      setSelectedType(null);
      setFormData({ details: {} });
    }
  }, [isOpen, initialAccommodation]);

  /**
     * Type Selection Handler
     * 
     * Manages accommodation type selection and form reset.
     * 
     * @param {string} type - Selected accommodation type
     * @effects
     * - Resets form data
     * - Clears previous errors
     * - Updates selected type
     */
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData({ details: {} });
    setError('');
  };

  /**
     * Modal Close Handler
     * 
     * Manages clean modal closure with state reset.
     * 
     * @effects
     * - Resets all form state
     * - Clears errors
     * - Triggers onClose callback
     */
  const handleClose = () => {
    setSelectedType(null);
    setFormData({ details: {} });
    setError('');
    onClose();
  };

  /**
     * Hotel Form Submission Handler
     * 
     * Processes and validates hotel-specific form submissions.
     * Handles both new entries and updates to existing hotels.
     * 
     * @param {Object} hotelData - Hotel form data
     * @property {Object} hotelData.details - Hotel-specific details
     * @property {string} [hotelData.status] - Booking status
     * @property {Object} [hotelData.cost] - Cost information
     * 
     * @throws {Error} When save operation fails
     */
  const handleHotelSubmit = async (hotelData) => {
    try {
      console.log('Hotel data received:', hotelData);

      // Preserve existing summary if not explicitly changed
      const preserveSummary = initialAccommodation &&
        initialAccommodation.summary &&
        (!hotelData.summary || hotelData.summary === initialAccommodation.summary);

      // Construct normalized accommodation data object
      const accommodationData = {
        type: 'hotel',
        details: {
          name: hotelData.details.name,
          city: hotelData.details.city,
          address: hotelData.details.address,
          phone: hotelData.details.phone,
          url: hotelData.details.url,
          checkInDate: hotelData.details.checkInDate,
          checkOutDate: hotelData.details.checkOutDate,
          roomType: hotelData.details.roomType,
          bookingAgent: hotelData.details.bookingAgent,
          bookingUrl: hotelData.details.bookingUrl,
          bookingReference: hotelData.details.bookingReference,
          notes: hotelData.details.notes
        },
        status: hotelData.status || 'Planned',
        summary: preserveSummary ? initialAccommodation.summary : hotelData.summary,
        cost: hotelData.cost || {
          amount: 0,
          currency: 'USD',
          paid: false
        }
      };

      console.log('Prepared accommodation data:', accommodationData);
      await onSave(accommodationData);
      handleClose();
    } catch (error) {
      setError('Failed to save hotel details. Please try again.');
      console.error('Error saving hotel:', error);
    }
  };

  /**
     * Generic Form Input Handler
     * 
     * Updates form state for non-hotel accommodation types.
     * Maintains nested structure of form data.
     * 
     * @param {string} fieldName - Name of the form field
     * @param {any} value - New field value
     */
  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [fieldName]: value
      }
    }));
  };

  /**
     * Generic Form Submission Handler
     * 
     * Processes form submissions for non-hotel accommodation types.
     * Includes validation and error handling.
     * 
     * @param {Event} e - Form submission event
     * @effects
     * - Validates required fields
     * - Sets error messages if validation fails
     * - Triggers save callback if validation passes
     */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedType) return;

    // Validate required fields
    const typeConfig = accommodationTypes[selectedType];
    const missingFields = Object.entries(typeConfig.fields)
      .filter(([fieldName, field]) =>
        field.required && !formData.details[fieldName])
      .map(([_, field]) => field.label);

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Prepare and save accommodation data
    const accommodationData = {
      type: selectedType,
      details: formData.details,
      status: 'Planned'
    };

    onSave(accommodationData);
    handleClose();
  };

  /**
     * Component Render
     * 
     * Renders a modal with dynamic content based on:
     * - Selected accommodation type
     * - Edit/create mode
     * - Form validation state
     */
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Add Accommodation"
      className="accommodation-modal"
      overlayClassName="accommodation-modal-overlay"
    >
      <div className="accommodation-modal-content">
        {/* Modal Title - Adapts to edit/create mode */}
        <h2 className="modal-title">
          {initialAccommodation ? 'Edit Accommodation' : 'Add Accommodation'}
        </h2>

        {/* Warning Banner - e.g., for AI-extracted data */}
        {warningBanner}

        {/* Error Display Section */}
        {error && <div className="error-message">{error}</div>}

        {/* Type Selection Section - Only shown for new accommodations */}
        {!selectedType && !initialAccommodation && (
          <div className="type-selection">
            <h3>Select Accommodation Type</h3>
            <div className="type-buttons">
              {Object.entries(accommodationTypes).map(([type, config]) => (
                <button
                  key={type}
                  className="type-button"
                  onClick={() => handleTypeSelect(type)}
                >
                  <span className="type-icon">{config.icon && <config.icon />}</span>
                  <span className="type-label">{config.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hotel-specific Form Component */}
        {selectedType === 'hotel' && (
          <AddHotelForm
            onSubmit={handleHotelSubmit}
            initialData={initialAccommodation?.details || {}}
            onCancel={handleClose}
          />
        )}

        {/* Dynamic Form Generation for Other Accommodation Types */}
        {selectedType && !['hotel'].includes(selectedType) && (
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>{accommodationTypes[selectedType]?.label || 'Accommodation'} Details</h3>

              {/* Dynamic Field Generation */}
              {accommodationTypes[selectedType] && Object.entries(accommodationTypes[selectedType].fields).map(([fieldName, field]) => (
                <div key={fieldName} className="form-group">
                  <label>{field.label}{field.required ? ' *' : ''}</label>
                  <input
                    type={field.type}
                    value={formData.details[fieldName] || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    required={field.required}
                  />
                </div>
              ))}

              {/* Form Action Buttons */}
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleClose}>Cancel</button>
                <button type="submit" className="save-button">Save</button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default AddAccommodation; 